import React, { useEffect, useState } from "react";
import { auth, db } from "./firebaseConfig";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import StudyDenLogo from "./assets/images/branding/Studyden_Primary.svg";
import StudyDenWhiteBg from "./assets/images/branding/Studyden_Primary.svg";
import { requiresForcedPasswordChange } from "./utils/forcedPasswordChange";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

const Login = ({ isProfilePage = false }) => {
  const currentUser = isProfilePage ? auth.currentUser : null;
  const isGoogleUser =
    currentUser &&
    currentUser.providerData &&
    currentUser.providerData.length > 0 &&
    currentUser.providerData[0].providerId === "google.com";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState({ text: "", isError: false });

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const getPostLoginDestination = async (firebaseUser) => {
    const userDocRef = doc(db, "users", firebaseUser.uid);
    const userDocSnap = await getDoc(userDocRef);
    const userData = userDocSnap.exists() ? userDocSnap.data() : {};

    return requiresForcedPasswordChange(userData)
      ? "/change-password-required"
      : "/dashboard";
  };

  /* ---- auth handlers (unchanged) ---- */
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage({
        text: "If an account exists for that email, a reset link has been sent.",
        isError: false,
      });
      setResetEmail("");
    } catch (error) {
      console.error("Error sending password reset email:", error.message);
      setResetMessage({ text: error.message, isError: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("User signed in:", userCredential.user.email);
      // App owns the post-authentication transition. Its auth/profile listener
      // keeps the branded loading state mounted and redirects only once the
      // authoritative account profile is ready.
    } catch (error) {
      console.error("Error logging in:", error.message);
      alert("Invalid email or password. Please try again.");
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Google user signed in:", user.displayName, user.email);

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // Create a minimal document — do NOT assign role: "student" here.
        // Role is assigned by the backend after the user completes onboarding.
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName || "Google User",
          extraTimePercentage: 0,
          createdAt: serverTimestamp(),
        });
        console.log("New Google user document created with createdAt timestamp");
      }

      const destination = await getPostLoginDestination(user);
      navigate(destination, { replace: true });
    } catch (error) {
      console.error("Error during Google Sign-In:", error.message);
    }
  };

  const IconBook = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4.5A2.5 2.5 0 0 1 4.5 2H11v18H4.5A2.5 2.5 0 0 0 2 22.5z" />
      <path d="M22 4.5A2.5 2.5 0 0 0 19.5 2H13v18h6.5a2.5 2.5 0 0 1 2.5 2.5z" />
    </svg>
  );

  const IconClipboard = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="18" rx="2" />
      <path d="M9 4V2h6v2" />
      <path d="m8 12 2 2 5-5" />
      <path d="M8 18h8" />
    </svg>
  );

  const IconProgress = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="m3 8 6-5 6 6 6-6" />
      <path d="M17 3h4v4" />
    </svg>
  );

  const IconShield = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );

  const IconCloud = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19H6a4 4 0 0 1-.7-7.94A7 7 0 0 1 18.72 9.2 5 5 0 0 1 17.5 19z" />
    </svg>
  );

  const IconLearning = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 9-6 9 6-9 6z" />
      <path d="M7 14v4c3 2 7 2 10 0v-4" />
    </svg>
  );

  const IconEmail = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );

  const IconLock = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );

  return (
    <div className="studyden-login">
      <main className="sd-auth-layout">
        <section className="sd-brand-panel" aria-labelledby="sd-brand-heading">
          <div className="sd-brand-glow" aria-hidden="true" />
          <div className="sd-brand-content">
            <div className="sd-brand-logo-wrap">
              <img src={StudyDenLogo} alt="StudyDen — Learn. Assess. Progress." className="sd-brand-logo" />
            </div>
            <p className="sd-brand-positioning">AI-powered learning &amp; assessment</p>

            <div className="sd-brand-copy">
              <h1 id="sd-brand-heading">Everything learning<br />in <span>one place.</span></h1>
              <p>
                StudyDen brings together learning, assessment, feedback and progress
                tracking in one intelligent, AI-powered platform for educators and learners.
              </p>
            </div>

            <div className="sd-features" aria-label="StudyDen platform benefits">
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconBook /></span>
                <div><h2>Learn</h2><p>Access and engage with rich learning content.</p></div>
              </div>
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconClipboard /></span>
                <div><h2>Assess</h2><p>Complete assignments and assessments with confidence.</p></div>
              </div>
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconProgress /></span>
                <div><h2>Progress</h2><p>Track performance and achieve your goals.</p></div>
              </div>
            </div>
          </div>
        </section>

        <section className="sd-form-panel" aria-labelledby="sd-login-title">
          <div className="sd-form-wrap">
            <img src={StudyDenWhiteBg} alt="StudyDen — Learn. Assess. Progress." className="sd-mobile-logo" />
            <header className="sd-form-header">
              <p className="sd-eyebrow">Welcome to StudyDen</p>
              <h2 id="sd-login-title">Welcome back</h2>
              <p>Sign in to your StudyDen account</p>
            </header>

            <form className="sd-login-form" onSubmit={handleSubmit}>
              <div className="sd-field">
                <label htmlFor="login-email">Email</label>
                <div className="sd-input-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconEmail /></span>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="sd-field">
                <div className="sd-password-label-row">
                  <label htmlFor="login-password">Password</label>
                  {(!isProfilePage || (isProfilePage && !isGoogleUser)) && (
                    <button
                      type="button"
                      className="sd-forgot-button"
                      aria-expanded={showForgotPassword}
                      aria-controls="password-reset-panel"
                      onClick={() => setShowForgotPassword(!showForgotPassword)}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="sd-input-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconLock /></span>
                  <input
                    id="login-password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
              </div>

              <button type="submit" className="sd-primary-button">Sign In</button>
            </form>

            {showForgotPassword && (
              <div className="sd-reset-panel" id="password-reset-panel">
                <p>Enter your email and we’ll send you a password reset link.</p>
                <form onSubmit={handlePasswordReset}>
                  <label className="sd-visually-hidden" htmlFor="reset-email">Password reset email</label>
                  <div className="sd-input-wrap">
                    <span className="sd-input-icon" aria-hidden="true"><IconEmail /></span>
                    <input
                      id="reset-email"
                      name="reset-email"
                      type="email"
                      placeholder="Enter your email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <button type="submit" className="sd-reset-button">Send reset link</button>
                </form>
                {resetMessage.text && (
                  <div
                    className={`sd-reset-message ${resetMessage.isError ? "error" : "success"}`}
                    role={resetMessage.isError ? "alert" : "status"}
                    aria-live="polite"
                  >
                    {resetMessage.text}
                  </div>
                )}
              </div>
            )}

            <div className="sd-divider" aria-hidden="true"><span>or</span></div>

            <button type="button" className="sd-google-button" onClick={handleGoogleSignIn}>
              <img src="https://developers.google.com/identity/images/g-logo.png" alt="" />
              Sign in with Google
            </button>

            <p className="sd-signup-copy">
              Don&apos;t have an account?{" "}
              <button type="button" onClick={() => navigate("/signup")}>Get started</button>
            </p>
          </div>
        </section>
      </main>

      <aside className="sd-trust-strip" aria-label="Why choose StudyDen">
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconShield /></span>
          <div><h2>Secure &amp; Private</h2><p>Your data is safe and protected.</p></div>
        </div>
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconCloud /></span>
          <div><h2>Accessible Anywhere</h2><p>Learn and assess from any device.</p></div>
        </div>
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconLearning /></span>
          <div><h2>Built by Educators</h2><p>Designed by educators, for educators.</p></div>
        </div>
      </aside>

      <footer className="sd-footer">&copy; 2026 StudyDen. All rights reserved.</footer>
    </div>
  );
};

export default Login;
